import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BRAIN_API, getCsrfToken, fmtDate } from "../lib/founderApi";
import { Panel, Row, EMPTY } from "../components/founder/Panel";
import { SessionMeter } from "../components/founder/SessionMeter";
import { LogDecisionForm } from "../components/founder/LogDecisionForm";
import { LoginView } from "../components/founder/LoginView";

const PANELS = [
  {
    key: "recent_decisions", title: "Recent decisions", testId: "panel-decisions",
    row: (d) => ({ id: d.brain_id, primary: d.payload.title, secondary: d.payload.decision,
      meta: `${fmtDate(d.created_at)} · v${d.version} · ${d.payload.reversibility}` }),
  },
  {
    key: "recent_overrides", title: "Overrides issued", testId: "panel-overrides",
    row: (d) => ({ id: d.brain_id, primary: d.payload.founder_decision, secondary: d.payload.precedent,
      meta: `${fmtDate(d.created_at)} · ${d.payload.applies_to_future ? "precedent-setting" : "one-off"}` }),
  },
  {
    key: "voice_themes", title: "Voice of user — top themes", testId: "panel-voice",
    row: (t) => ({ id: t.theme, primary: t.theme,
      meta: `${t.count} mentions · avg sentiment ${t.avg_sentiment?.toFixed(2)}` }),
  },
  {
    key: "competitive_intel", title: "Competitive intel", testId: "panel-intel",
    row: (d) => ({ id: d.brain_id, primary: d.payload.entity_name, secondary: d.payload.observation,
      meta: `impact: ${d.payload.strategic_impact}` }),
  },
  {
    key: "veto_activity", title: "M7 veto activity", testId: "panel-vetoes",
    row: (d) => ({ id: d.brain_id, primary: d.payload.subject, secondary: d.payload.legal_basis,
      meta: d.payload.outcome }),
  },
  {
    key: "deferred_opportunities", title: "Deferred opportunities", testId: "panel-deferred",
    row: (d) => ({ id: d.brain_id, primary: d.payload.opportunity.slice(0, 120),
      secondary: `Reconsider: ${d.payload.earliest_reconsideration}`, meta: d.payload.estimated_value }),
  },
  {
    key: "kill_conditions", title: "Kill conditions", testId: "panel-kills",
    row: (d) => ({ id: d.brain_id, primary: d.payload.condition, secondary: d.payload.details,
      meta: `${d.payload.severity} · ${fmtDate(d.created_at)}` }),
  },
  {
    key: "founder_patterns", title: "Founder patterns", testId: "panel-patterns",
    row: (d) => ({ id: d.brain_id, primary: d.payload.description,
      meta: `${d.payload.pattern_type} · confidence ${d.payload.confidence} · v${d.version}` }),
  },
];

export default function FounderBrainPage() {
  const [params, setParams] = useSearchParams();
  const [authed, setAuthed] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const loadDashboard = useCallback(async () => {
    const res = await fetch(`${BRAIN_API}/founder/dashboard`, {
      credentials: "include",
    });
    if (res.status === 401) {
      setAuthed(false);
      setData(null);
      return;
    }
    setData(await res.json());
    setAuthed(true);
  }, []);

  useEffect(() => {
    const magic = params.get("token");
    if (!magic) {
      loadDashboard();
      return;
    }
    (async () => {
      const res = await fetch(`${BRAIN_API}/founder/session`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: magic }),
      });
      if (!res.ok) {
        setError("That link is invalid or expired. Request a new one.");
      }
      setParams({}, { replace: true });
    })();
  }, [params, setParams, loadDashboard]);

  const signOut = useCallback(async () => {
    await fetch(`${BRAIN_API}/founder/logout`, {
      method: "POST",
      credentials: "include",
      headers: { "X-CSRF-Token": getCsrfToken() },
    });
    setAuthed(false);
    setData(null);
    setError("");
  }, []);

  if (authed === false) {
    return (
      <>
        {error && (
          <p data-testid="magic-link-error" className="bg-[#0B3D5C] text-white text-sm text-center py-2">{error}</p>
        )}
        <LoginView />
      </>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center">
        <p className="text-[#4A4A4A]" data-testid="dashboard-loading">Reading the Brain…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2] px-6 py-10" data-testid="founder-dashboard">
      <div className="max-w-6xl mx-auto">
        <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0B3D5C] mb-2">
              LeadRiver · Company Brain
            </p>
            <h1 className="text-4xl font-extrabold tracking-tight">Founder dashboard.</h1>
          </div>
          <div className="text-right">
            <p className="text-3xl font-extrabold text-[#E07B3A]" data-testid="total-entries">
              {data.totals.entries}
            </p>
            <p className="text-xs text-[#4A4A4A]">entries in the Brain</p>
            <SessionMeter expiresAt={data.session_expires_at} />
            <button
              data-testid="founder-signout"
              onClick={signOut}
              className="text-xs underline text-[#8A8378] mt-1"
            >
              Sign out
            </button>
          </div>
        </header>

        <div className="mb-6">
          <LogDecisionForm onLogged={loadDashboard} />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {PANELS.map(({ key, title, testId, row }) => (
            <Panel key={key} title={title} testId={testId} count={data[key].length}>
              {data[key].length
                ? data[key].map((item) => {
                    const r = row(item);
                    return <Row key={r.id} primary={r.primary} secondary={r.secondary} meta={r.meta} />;
                  })
                : EMPTY}
            </Panel>
          ))}
        </div>
      </div>
    </div>
  );
}
